import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import { FlaskIcon } from '@phosphor-icons/react';
import type {
  PreviewedInvoiceExceptions,
  SuggestedExceptionTestInvoice,
} from '@erp/rpc';
import {
  usePreviewAPInvoiceExceptions,
  useSuggestExceptionTestInvoices,
} from '@/api/rpc/hooks/accounts-payable/usePreviewAPInvoiceExceptions';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  errorText,
  invoiceBenchCardState,
  previewByInvoiceId,
} from '@/components/accounts-payable/exceptions/testExceptionRules';
import {
  canTestDraft,
  draftGuidanceFromRules,
  toCreateInput,
  type RuleSetDraft,
} from './exceptionRuleSetDraft';

/**
 * Test bench on the rule-set editor: curated unmatched open invoices plus
 * matched controls, fact card always visible, then a dry-run of the draft.
 */
export function TestRuleSetOnInvoicesAction({
  draft,
  savedRuleSetId,
}: {
  draft: RuleSetDraft;
  savedRuleSetId: string | null;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        variant="outline"
        disabled={!canTestDraft(draft)}
        onClick={() => setOpen(true)}
      >
        <FlaskIcon className="mr-1.5 h-4 w-4" />
        <T context="exception rules: open the draft test bench">
          Test on open invoices
        </T>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
          {open && (
            <TestRuleSetOnInvoicesBody
              draft={draft}
              savedRuleSetId={savedRuleSetId}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function TestRuleSetOnInvoicesBody({
  draft,
  savedRuleSetId,
}: {
  draft: RuleSetDraft;
  savedRuleSetId: string | null;
}) {
  const suggest = useSuggestExceptionTestInvoices(
    { unmatchedLimit: 4, controlLimit: 2 },
    { enabled: true },
  );
  const preview = usePreviewAPInvoiceExceptions();
  const byInvoice = previewByInvoiceId(preview.data);
  const invoices = suggest.data?.invoices ?? [];

  function run() {
    if (!draft.exceptionEnumId || invoices.length === 0) return;
    const problemFree = draft.rules.every(
      (rule) => rule.exceptionTypeOptionId && rule.ownerId,
    );
    preview.mutate({
      invoiceIds: invoices.map((invoice) => invoice.invoiceId),
      exceptionRuleSetId: savedRuleSetId ?? undefined,
      exceptionEnumId: draft.exceptionEnumId,
      rules: problemFree ? toCreateInput(draft).rules : undefined,
      draftGuidance: draftGuidanceFromRules(draft.rules),
    });
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="exception rules: test bench dialog title">
            Test draft on open invoices
          </T>
        </DialogTitle>
        <DialogDescription>
          <T context="exception rules: test bench dialog description">
            Unmatched open invoices first (failed or unfinished match), then
            fully matched controls. The fact card is what the evaluator sees.
            Nothing is saved on the invoices.
          </T>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 py-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {suggest.isLoading ? (
              <T context="exception rules: loading curated test invoices">
                Loading example invoices…
              </T>
            ) : (
              <T context="exception rules: curated invoice count">
                <Var>{invoices.length}</Var> curated invoices
              </T>
            )}
          </p>
          <Button
            size="sm"
            disabled={preview.isPending || invoices.length === 0}
            onClick={run}
          >
            {preview.isPending ? (
              <T context="exception rules: running evaluator on the bench">
                Running evaluator…
              </T>
            ) : (
              <T context="exception rules: run evaluator on the bench">
                Run evaluator
              </T>
            )}
          </Button>
        </div>
        {suggest.isError && (
          <p className="text-xs text-destructive">{errorText(suggest.error)}</p>
        )}
        {preview.isError && (
          <p className="text-xs text-destructive">{errorText(preview.error)}</p>
        )}
        {invoices.map((invoice) => (
          <InvoiceBenchCard
            key={invoice.invoiceId}
            invoice={invoice}
            result={byInvoice.get(invoice.invoiceId)}
          />
        ))}
      </div>
    </>
  );
}

function InvoiceBenchCard({
  invoice,
  result,
}: {
  invoice: SuggestedExceptionTestInvoice;
  result: PreviewedInvoiceExceptions | undefined;
}) {
  const gt = useGT();
  const state = invoiceBenchCardState(
    invoice,
    result,
    gt('Unknown supplier', {
      context: 'exception rules test bench: invoice has no supplier',
    }),
  );
  return (
    <section className="space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{state.displayNumber}</span>
        <span className="text-xs text-muted-foreground">
          {state.supplierName} · {state.status} · {state.totalAmount}{' '}
          {state.currency}
        </span>
        <WhySelectedBadge reason={state.whySelected} />
      </div>
      <p className="text-xs text-muted-foreground">
        <T context="exception rules: matching summary on a test invoice">
          Matching: <Var>{state.matching.matchedLineCount}</Var>/
          <Var>{state.matching.matchableLineCount}</Var> matched,{' '}
          <Var>{state.matching.unmatchedLineCount}</Var> unmatched
        </T>
      </p>
      <InvoiceBenchVerdict state={state} />
      <pre className="max-h-72 overflow-auto rounded-md bg-muted p-3 text-[11px] leading-snug whitespace-pre-wrap">
        {state.factsText}
      </pre>
    </section>
  );
}

function InvoiceBenchVerdict({
  state,
}: {
  state: ReturnType<typeof invoiceBenchCardState>;
}) {
  if (state.verdict === 'pending') return null;
  if (state.verdict === 'clear') {
    return (
      <p className="rounded-md border border-green-200 bg-green-50 px-2 py-1.5 text-xs dark:border-green-900 dark:bg-green-950/30">
        <T context="exception rules: bench verdict when nothing fires">
          No exceptions. A live pass would let this invoice proceed.
        </T>
      </p>
    );
  }
  return (
    <div className="space-y-1">
      <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs dark:border-amber-900 dark:bg-amber-950/30">
        {state.ownerTypeName ? (
          <T context="exception rules: bench verdict when blocked with owner">
            Blocked. <Var>{state.ownerTypeName}</Var> →{' '}
            <Var>{state.ownerDisplay}</Var>
          </T>
        ) : (
          <T context="exception rules: bench verdict when blocked unassigned">
            Blocked, unassigned.
          </T>
        )}
      </p>
      <ul className="text-xs">
        {state.raised.map((exception) => (
          <li key={exception.exceptionTypeOptionId}>
            <span className="font-medium">{exception.name}</span>
            {' — '}
            {exception.reasoning}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WhySelectedBadge({
  reason,
}: {
  reason: SuggestedExceptionTestInvoice['whySelected'];
}) {
  if (reason === 'unmatched-open') {
    return (
      <Badge variant="outline">
        <T context="badge: curated unmatched open invoice">Unmatched open</T>
      </Badge>
    );
  }
  return (
    <Badge variant="secondary">
      <T context="badge: curated fully matched control invoice">
        Matched control
      </T>
    </Badge>
  );
}
